import { createSlice } from '@reduxjs/toolkit';
import { FormAccordion } from '@strata/react-form-section';
import { v4 as uuidv4 } from 'uuid';

import { handleError } from 'services/Error';
import { resetQuestionTree } from 'store/referralForm/questionSlice';
import { FormSectionDataSchema, FormSections } from 'store/referralForm/referralFormSchema';

// Set up initial form state
const initSections = () => {
    const sections = {};
    Object.values(FormSections).forEach((section) => {
        sections[section] = {
            progressIndicatorStatus:
                section === 1 ? FormAccordion.statuses.ACTIVE : FormAccordion.statuses.NOT_STARTED,
            isOpen: section === 1,
        };
    });
    return sections;
};
const initCapturedDataSchema = () => {
    let capturedDataSchema = {};
    Object.values(FormSections).forEach((section) => {
        capturedDataSchema = { ...capturedDataSchema, ...FormSectionDataSchema[section] };
    });
    return capturedDataSchema;
};

const initialState = {
    capturedData: initCapturedDataSchema(),
    sections: initSections(),
    activeSection: 1,
};

export const referralFormSlice = createSlice({
    name: 'referralForm',
    initialState,
    reducers: {
        resetState(_state, _action) {
            return initialState;
        },
        updateActiveSection(state, action) {
            return {
                ...state,
                activeSection: action.payload.sectionId,
            };
        },
        updateCapturedData(state, action) {
            return {
                ...state,
                capturedData: {
                    ...state.capturedData,
                    ...action.payload,
                },
            };
        },
        updateSectionStatus(state, action) {
            return {
                ...state,
                sections: {
                    ...state.sections,
                    [action.payload.sectionId]: {
                        ...state.sections[action.payload.sectionId],
                        ...action.payload.sectionStatus,
                    },
                },
            };
        },
    },
});

export const { resetState, updateActiveSection, updateCapturedData, updateSectionStatus } =
    referralFormSlice.actions;

export const resetForm = () => (dispatch) => {
    dispatch(resetState());
    dispatch(resetQuestionTree());
};

export const setClinicalPolicyAnswers =
    (clinicalPolicyAnswers, clinicalPolicyException) => (dispatch) => {
        dispatch(
            updateCapturedData({
                clinicalPolicyAnswers,
                clinicalPolicyException,
            }),
        );
    };

export const clearClinicalPolicyAnswers = () => (dispatch) => {
    dispatch(
        updateCapturedData({
            clinicalPolicyAnswers: [],
            clinicalPolicyException: undefined,
        }),
    );
};

export const setActiveSection = (step) => (dispatch) => {
    dispatch(
        updateActiveSection({
            sectionId: step,
        }),
    );
};

export const completeSection =
    (step, hasException = false) =>
    (dispatch) => {
        const payload = {
            sectionId: step,
            sectionStatus: {
                progressIndicatorStatus: hasException
                    ? FormAccordion.statuses.WARNING
                    : FormAccordion.statuses.COMPLETED,
            },
        };
        dispatch(updateSectionStatus(payload));
    };

export const enableSection = (step) => (dispatch) => {
    const payload = {
        sectionId: step,
        sectionStatus: { progressIndicatorStatus: FormAccordion.statuses.ACTIVE },
    };
    dispatch(updateSectionStatus(payload));
};

export const disableSection = (step) => (dispatch) => {
    const payload = {
        sectionId: step,
        sectionStatus: {
            isOpen: false,
            progressIndicatorStatus: FormAccordion.statuses.NOT_STARTED,
        },
    };
    dispatch(updateSectionStatus(payload));
};

export const openSection = (step) => (dispatch) => {
    const payload = {
        sectionId: step,
        sectionStatus: { isOpen: true },
    };
    dispatch(updateSectionStatus(payload));
};

export const closeSection = (step) => (dispatch) => {
    const payload = {
        sectionId: step,
        sectionStatus: { isOpen: false },
    };
    dispatch(updateSectionStatus(payload));
};

export const nextSection = (step, hasException) => (dispatch, getState) => {
    // Reset sections after next section if completing section 1, 2, or 5 after later sections have already been filled out
    if (
        getState().referralForm.activeSection !== step &&
        (step <= FormSections.MEDICATION_AND_DIAGNOSIS ||
            step === FormSections.PLACE_OF_SERVICE_AND_DELIVERY)
    ) {
        let dataToReset = {};
        Object.values(FormSections).forEach((section) => {
            if (step < section) {
                dataToReset = { ...dataToReset, ...FormSectionDataSchema[section] };
                dispatch(disableSection(section));
            }
        });
        dispatch(updateCapturedData(dataToReset));
        dispatch(resetQuestionTree());
    }

    dispatch(closeSection(step));
    dispatch(completeSection(step, hasException));
    dispatch(openSection(step + 1));
    dispatch(enableSection(step + 1));
    dispatch(setActiveSection(step + 1));
};

export const reauthReferral = (referralDetails) => async (dispatch) => {
    try {
        dispatch(resetForm());

        const {
            prescriberNpi,
            payerId,
            memberFirstName,
            memberId,
            memberDOB,
            productServiceOid,
            medication,
            diagnosisOid,
            diagnosisCode,
            referralId,
        } = referralDetails;

        dispatch(
            updateCapturedData({
                prescriberNpi,
                payerId,
                firstName: memberFirstName,
                memberId,
                memberDOB,
                pendingReauth: true,
                referralId: uuidv4(),
                productServiceOid,
                medication,
                diagnosisOid,
                diagnosisCode,
                renewsReferralId: referralId,
            }),
        );
    } catch (err) {
        handleError('An error occurred during referral reauth.', err);
        dispatch(resetForm());
    }
};
